import { useState, useEffect, useCallback } from "react";
import { useForm, Controller } from "react-hook-form";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
    Card,
    CardBody,
    CardHeader,
    CardFooter,
    Button,
    Autocomplete,
    AutocompleteItem,
    DatePicker,
    NumberInput,
    Textarea,
    Spinner,
    Chip,
} from "@heroui/react";
import { LuArrowLeft, LuArrowRight, LuSave, LuTriangleAlert } from "react-icons/lu";
import axios from "axios";
import { usePostData, useFetchData } from "@/hooks";
import { showToast, formatThousands } from "@/utils";
import { ThemedText, PageHeader, ErrorState } from "@/components/ui";
import { parseDate, getLocalTimeZone, today } from "@internationalized/date";
import { BASE_API } from "@/constants";

const EggConversionCreate = () => {
    const { batchId } = useParams();
    const isEdit = Boolean(batchId);
    const navigate = useNavigate();

    const [unitUrl, setUnitUrl] = useState(null);
    const [preview, setPreview] = useState({
        destMaterial: null,
        availableStock: null,
        checking: false,
    });
    // For edit: the source stock is refunded on revert, so add the original qty back.
    const [initialSourceKey, setInitialSourceKey] = useState("");
    const [initialQty, setInitialQty] = useState(0);

    const {
        control,
        handleSubmit,
        watch,
        setValue,
        reset,
        formState: { errors },
    } = useForm({
        defaultValues: {
            warehouse_id: "",
            source_material_id: "",
            source_material_unit_id: "",
            dest_material_id: "",
            date: isEdit ? null : today(getLocalTimeZone()).toString(),
            quantity: null,
            description: "",
        },
    });

    const watchData = watch();

    const { data: materials, isLoading: isLoadingMaterials } =
        useFetchData("select/material");
    const { data: warehouses, isLoading: isLoadingWarehouses } =
        useFetchData("select/warehouse");
    const { data: units, isLoading: isLoadingUnits } = useFetchData(unitUrl);

    const {
        data: editData,
        isLoading: isFetching,
        error: fetchError,
    } = useFetchData(isEdit ? `egg-conversions/${batchId}/edit` : null);

    const storeMutation = usePostData("egg-conversions/store");
    const updateMutation = usePostData(`egg-conversions/${batchId}/update`);

    const sourceKey = (d) =>
        [d.warehouse_id || "", d.source_material_id || "", d.source_material_unit_id || ""].join("|");

    // Fetch dest-material resolution + available source stock.
    const refreshPreview = useCallback(async (d) => {
        if (!d.source_material_id) {
            setPreview({ destMaterial: null, availableStock: null, checking: false });
            return;
        }
        setPreview((p) => ({ ...p, checking: true }));
        try {
            const res = await axios.get(`${BASE_API}/egg-conversions/preview`, {
                params: {
                    source_material_id: d.source_material_id,
                    warehouse_id: d.warehouse_id || undefined,
                    source_material_unit_id: d.source_material_unit_id || undefined,
                },
            });
            const data = res.data?.data || {};
            setPreview({
                destMaterial: data.dest_material || null,
                availableStock:
                    data.available_stock === null ||
                    data.available_stock === undefined
                        ? null
                        : Number(data.available_stock),
                checking: false,
            });
        } catch {
            setPreview({ destMaterial: null, availableStock: null, checking: false });
        }
    }, []);

    // Populate form in edit mode.
    useEffect(() => {
        if (!editData) return;
        const formData = {
            warehouse_id: editData.warehouse_id ? String(editData.warehouse_id) : "",
            source_material_id: editData.source_material_id
                ? String(editData.source_material_id)
                : "",
            source_material_unit_id: editData.source_material_unit_id
                ? String(editData.source_material_unit_id)
                : "",
            dest_material_id: "",
            date: editData.date || null,
            quantity: editData.quantity || null,
            description: editData.description || "",
        };
        reset(formData);
        if (editData.source_material_id) {
            setUnitUrl(
                `select/material-unit?material_id=${editData.source_material_id}`,
            );
        }
        setInitialSourceKey(sourceKey(formData));
        setInitialQty(Number(editData.quantity || 0));
        refreshPreview(formData);
    }, [editData, reset, refreshPreview]);

    const handleSourceChange = (key) => {
        setValue("source_material_id", key || "");
        setValue("source_material_unit_id", "");
        setValue("dest_material_id", "");
        setUnitUrl(key ? `select/material-unit?material_id=${key}` : null);
        refreshPreview({
            ...watchData,
            source_material_id: key || "",
            source_material_unit_id: "",
        });
    };

    const handleWarehouseChange = (key) => {
        setValue("warehouse_id", key || "");
        refreshPreview({ ...watchData, warehouse_id: key || "" });
    };

    const handleUnitChange = (key) => {
        setValue("source_material_unit_id", key || "");
        refreshPreview({ ...watchData, source_material_unit_id: key || "" });
    };

    // Effective available stock (edit refunds the original quantity when source unchanged).
    const effectiveAvailable = () => {
        if (preview.availableStock === null) return null;
        const base = preview.availableStock;
        return isEdit && sourceKey(watchData) === initialSourceKey
            ? base + initialQty
            : base;
    };

    const onSubmit = (data) => {
        // Manual destination is required only when auto-resolve failed.
        if (!preview.destMaterial && !data.dest_material_id) {
            showToast(
                "error",
                "Telur Pecah Tujuan Belum Ada",
                "Material telur pecah pasangan tidak ditemukan. Pilih tujuan secara manual.",
            );
            return;
        }

        const available = effectiveAvailable();
        if (available !== null && Number(data.quantity || 0) > available) {
            showToast(
                "error",
                "Stok Tidak Cukup",
                `Stok telur sehat tersedia ${available}, dibutuhkan ${data.quantity}.`,
            );
            return;
        }

        const payload = {
            date: data.date,
            warehouse_id: data.warehouse_id,
            source_material_id: data.source_material_id,
            source_material_unit_id: data.source_material_unit_id,
            quantity: Number(data.quantity),
            description: data.description || null,
            ...(data.dest_material_id
                ? { dest_material_id: data.dest_material_id }
                : {}),
        };

        const mutation = isEdit ? updateMutation : storeMutation;
        mutation.mutate(payload, {
            method: isEdit ? "put" : "post",
            onSuccess: () => {
                showToast(
                    "success",
                    "Berhasil",
                    isEdit
                        ? "Konversi telur berhasil diperbarui."
                        : "Konversi telur berhasil disimpan.",
                );
                navigate("/egg-conversion");
            },
            onError: (err) => {
                const message =
                    err.response?.status === 422
                        ? err.response?.data?.message ||
                          Object.values(err.response?.data?.errors || {})?.[0]?.[0] ||
                          "Mohon periksa kembali input Anda."
                        : "Terjadi kesalahan pada sistem. Silakan coba beberapa saat lagi.";
                showToast("error", "Gagal", message);
            },
        });
    };

    if (fetchError) {
        return (
            <ErrorState
                title="Gagal Memuat Data"
                description="Terjadi kesalahan saat memuat data konversi."
                onAction={() => navigate("/egg-conversion")}
                actionLabel="Kembali"
            />
        );
    }

    const available = effectiveAvailable();
    const unitLabel =
        (units || []).find(
            (u) => String(u.value) === String(watchData.source_material_unit_id),
        )?.title || "";

    return (
        <div className="mx-auto flex max-w-5xl flex-col gap-6">
            <div className="flex gap-4">
                <Button isIconOnly variant="light" onPress={() => navigate(-1)}>
                    <LuArrowLeft size={20} />
                </Button>
                <PageHeader
                    title={isEdit ? "Edit Konversi Telur" : "Tambah Konversi Telur"}
                    breadcrumbs={[
                        { label: "Dashboard", href: "/" },
                        { label: "Konversi Telur", href: "/egg-conversion" },
                        { label: isEdit ? "Edit" : "Tambah" },
                    ]}
                />
            </div>

            <form onSubmit={handleSubmit(onSubmit)} noValidate>
                <Card className="border-default-200 border" shadow="none">
                    <CardHeader className="bg-default-100/70 px-6 py-4">
                        <ThemedText className="font-semibold">
                            Detail Konversi (Telur Sehat → Telur Pecah)
                        </ThemedText>
                    </CardHeader>
                    <CardBody className="p-6">
                        {isEdit && isFetching ? (
                            <div className="flex h-48 w-full items-center justify-center">
                                <Spinner label="Memuat data..." color="primary" />
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                <Controller
                                    name="warehouse_id"
                                    control={control}
                                    rules={{ required: "Gudang wajib dipilih" }}
                                    render={({ field }) => (
                                        <Autocomplete
                                            label="Gudang"
                                            placeholder="Pilih gudang"
                                            isRequired
                                            isLoading={isLoadingWarehouses}
                                            selectedKey={
                                                field.value
                                                    ? String(field.value)
                                                    : null
                                            }
                                            onSelectionChange={(key) => {
                                                field.onChange(key || "");
                                                handleWarehouseChange(key);
                                            }}
                                            variant="bordered"
                                            isInvalid={!!errors.warehouse_id}
                                            errorMessage={
                                                errors.warehouse_id?.message
                                            }
                                        >
                                            {(warehouses || []).map((w) => (
                                                <AutocompleteItem
                                                    key={String(w.value)}
                                                >
                                                    {w.title}
                                                </AutocompleteItem>
                                            ))}
                                        </Autocomplete>
                                    )}
                                />

                                <Controller
                                    name="date"
                                    control={control}
                                    rules={{ required: "Tanggal wajib diisi" }}
                                    render={({ field }) => (
                                        <DatePicker
                                            label="Tanggal"
                                            isRequired
                                            showMonthAndYearPickers
                                            value={
                                                field.value
                                                    ? parseDate(field.value)
                                                    : null
                                            }
                                            onChange={(date) =>
                                                field.onChange(
                                                    date ? date.toString() : "",
                                                )
                                            }
                                            maxValue={today(getLocalTimeZone())}
                                            variant="bordered"
                                            isInvalid={!!errors.date}
                                            errorMessage={errors.date?.message}
                                        />
                                    )}
                                />

                                <Controller
                                    name="source_material_id"
                                    control={control}
                                    rules={{
                                        required: "Telur sehat wajib dipilih",
                                    }}
                                    render={({ field }) => (
                                        <Autocomplete
                                            label="Telur Sehat (sumber)"
                                            placeholder="Cari & pilih telur sehat..."
                                            isRequired
                                            isLoading={isLoadingMaterials}
                                            selectedKey={
                                                field.value
                                                    ? String(field.value)
                                                    : null
                                            }
                                            onSelectionChange={(key) => {
                                                field.onChange(key || "");
                                                handleSourceChange(key);
                                            }}
                                            variant="bordered"
                                            isInvalid={
                                                !!errors.source_material_id
                                            }
                                            errorMessage={
                                                errors.source_material_id
                                                    ?.message
                                            }
                                        >
                                            {(materials || []).map((m) => (
                                                <AutocompleteItem
                                                    key={String(m.value)}
                                                >
                                                    {m.title}
                                                </AutocompleteItem>
                                            ))}
                                        </Autocomplete>
                                    )}
                                />

                                <Controller
                                    name="source_material_unit_id"
                                    control={control}
                                    rules={{ required: "Satuan wajib dipilih" }}
                                    render={({ field }) => (
                                        <Autocomplete
                                            label="Satuan"
                                            placeholder={
                                                watchData.source_material_id
                                                    ? "Pilih satuan"
                                                    : "Pilih telur dulu"
                                            }
                                            isRequired
                                            isDisabled={
                                                !watchData.source_material_id
                                            }
                                            isLoading={isLoadingUnits}
                                            selectedKey={
                                                field.value
                                                    ? String(field.value)
                                                    : null
                                            }
                                            onSelectionChange={(key) => {
                                                field.onChange(key || "");
                                                handleUnitChange(key);
                                            }}
                                            variant="bordered"
                                            isInvalid={
                                                !!errors.source_material_unit_id
                                            }
                                            errorMessage={
                                                errors.source_material_unit_id
                                                    ?.message
                                            }
                                        >
                                            {(units || []).map((u) => (
                                                <AutocompleteItem
                                                    key={String(u.value)}
                                                >
                                                    {u.title}
                                                </AutocompleteItem>
                                            ))}
                                        </Autocomplete>
                                    )}
                                />

                                <div className="flex flex-col gap-2">
                                    <Controller
                                        name="quantity"
                                        control={control}
                                        rules={{
                                            required: "Jumlah wajib diisi",
                                            min: {
                                                value: 0.01,
                                                message:
                                                    "Jumlah harus lebih dari 0",
                                            },
                                        }}
                                        render={({ field }) => (
                                            <NumberInput
                                                label="Jumlah Pecah"
                                                placeholder="Contoh: 10"
                                                isRequired
                                                isClearable
                                                value={field.value ?? undefined}
                                                onValueChange={(val) =>
                                                    field.onChange(val)
                                                }
                                                variant="bordered"
                                                minValue={0}
                                                hideStepper
                                                isWheelDisabled
                                                isInvalid={!!errors.quantity}
                                                errorMessage={
                                                    errors.quantity?.message
                                                }
                                                description={
                                                    preview.checking
                                                        ? "Mengecek stok tersedia..."
                                                        : undefined
                                                }
                                            />
                                        )}
                                    />
                                    {!preview.checking && available !== null && (
                                        <Chip
                                            size="sm"
                                            variant="flat"
                                            color={
                                                available > 0
                                                    ? "success"
                                                    : "warning"
                                            }
                                            className="w-fit"
                                        >
                                            Stok telur sehat tersedia:{" "}
                                            {formatThousands(available)}{" "}
                                            {unitLabel}
                                        </Chip>
                                    )}
                                </div>

                                {/* Destination preview / manual fallback */}
                                <div className="md:col-span-2">
                                    {preview.destMaterial ? (
                                        <div className="border-default-200 bg-default-50 flex items-center gap-2 rounded-lg border px-4 py-3">
                                            <ThemedText size="sm">
                                                {watchData.source_material_id
                                                    ? "Akan menambah stok"
                                                    : "Telur pecah tujuan"}
                                            </ThemedText>
                                            <LuArrowRight
                                                size={14}
                                                className="text-default-400"
                                            />
                                            <Chip
                                                size="sm"
                                                variant="flat"
                                                color="danger"
                                            >
                                                {preview.destMaterial.name}
                                            </Chip>
                                        </div>
                                    ) : watchData.source_material_id &&
                                      !preview.checking ? (
                                        <div className="flex flex-col gap-2">
                                            <div className="text-warning flex items-center gap-2">
                                                <LuTriangleAlert size={16} />
                                                <ThemedText
                                                    size="xs"
                                                    className="text-warning"
                                                >
                                                    Pasangan "… Pecah" tidak
                                                    ditemukan otomatis. Pilih
                                                    material tujuan secara
                                                    manual.
                                                </ThemedText>
                                            </div>
                                            <Controller
                                                name="dest_material_id"
                                                control={control}
                                                render={({ field }) => (
                                                    <Autocomplete
                                                        label="Telur Pecah (tujuan)"
                                                        placeholder="Pilih material tujuan"
                                                        selectedKey={
                                                            field.value
                                                                ? String(
                                                                      field.value,
                                                                  )
                                                                : null
                                                        }
                                                        onSelectionChange={(
                                                            key,
                                                        ) =>
                                                            field.onChange(
                                                                key || "",
                                                            )
                                                        }
                                                        variant="bordered"
                                                    >
                                                        {(materials || []).map(
                                                            (m) => (
                                                                <AutocompleteItem
                                                                    key={String(
                                                                        m.value,
                                                                    )}
                                                                >
                                                                    {m.title}
                                                                </AutocompleteItem>
                                                            ),
                                                        )}
                                                    </Autocomplete>
                                                )}
                                            />
                                        </div>
                                    ) : null}
                                </div>

                                <Controller
                                    name="description"
                                    control={control}
                                    render={({ field }) => (
                                        <Textarea
                                            label="Keterangan"
                                            placeholder="Keterangan tambahan (opsional)"
                                            value={field.value || ""}
                                            onValueChange={(val) =>
                                                field.onChange(val)
                                            }
                                            variant="bordered"
                                            className="md:col-span-2"
                                        />
                                    )}
                                />
                            </div>
                        )}
                    </CardBody>
                    <CardFooter className="flex justify-end gap-3 px-6 py-4">
                        <Button
                            as={Link}
                            href="/egg-conversion"
                            type="button"
                            variant="flat"
                            size="sm"
                        >
                            Batal
                        </Button>
                        <Button
                            color="primary"
                            type="submit"
                            isLoading={
                                storeMutation.isLoading ||
                                updateMutation.isLoading
                            }
                            startContent={<LuSave size={16} />}
                            size="sm"
                        >
                            {isEdit ? "Simpan Perubahan" : "Simpan Konversi"}
                        </Button>
                    </CardFooter>
                </Card>
            </form>
        </div>
    );
};

export default EggConversionCreate;
